<template>
  <div class="">
    <div class="flex flex-col gap-1 border-t border-gray-100 pt-3">
      <div>
        <div class="flex justify-between text-sm text-gray-600">
          <span>{{ t('personalization.summary.giftcardValue') }}</span>
          <span data-test-id="giftcard-summary-value">
            {{ formatAmount(giftcardValue) }}
          </span>
        </div>
        <span class="text-xs text-gray-400">
          {{
            quantity > 1
              ? t('personalization.summary.qtyTimesValue', {
                  quantity,
                  unitPrice: formatAmount(amount),
                })
              : t('personalization.summary.qty', { quantity })
          }}
        </span>
      </div>
      <div
        v-if="addons.hasGreetingCard"
        data-test-id="giftcard-summary-greeting-card"
      >
        <div class="flex justify-between text-sm text-gray-600">
          <span>{{ t('personalization.summary.greetingCard') }}</span>
          <span>{{ formatMoney(cardTotalPrice, locale, 'EUR', 2) }}</span>
        </div>
        <span class="text-xs text-gray-400">
          {{
            quantity > 1
              ? t('personalization.summary.qtyTimesValue', {
                  quantity,
                  unitPrice: formatMoney(cardPrice, locale, 'EUR', 2),
                })
              : t('personalization.summary.qty', { quantity })
          }}
        </span>
      </div>
      <div v-if="addons.hasGiftBox" data-test-id="giftcard-summary-gift-box">
        <div class="flex justify-between text-sm text-gray-600">
          <span>
            {{ t('personalization.summary.giftBox') }}
          </span>
          <span>{{ formatMoney(boxTotalPrice, locale, 'EUR', 2) }}</span>
        </div>
        <span class="text-xs text-gray-400">
          {{
            quantity > 1
              ? t('personalization.summary.qtyTimesValue', {
                  quantity,
                  unitPrice: formatMoney(boxPrice, locale, 'EUR', 2),
                })
              : t('personalization.summary.qty', { quantity })
          }}
        </span>
      </div>
    </div>

    <div
      class="mt-3 flex justify-between border-t border-gray-100 pt-3 text-base font-semibold text-black"
    >
      <span>{{ t('personalization.summary.total') }}</span>
      <span data-test-id="giftcard-summary-total">
        {{ formatAmount(total) }}
      </span>
    </div>

    <p class="mt-3 text-center text-xs text-gray-500">
      {{ t('personalization.summary.paymentMethodsLabel') }}
    </p>
    <CartPaymentOptions />
  </div>
</template>

<script setup lang="ts">
import CartPaymentOptions from '~/components/cart/CartPaymentOptions.vue'
import type { GiftcardAddonsSelection } from '~/components/giftcard/GiftcardAddonsStep.vue'
import { GIFTCARD_PERSONALIZATION_ADDON_PRICE_CENTS } from '~/utils/giftcardPersonalizationAddons'

const { t, locale } = useI18n()

const {
  amount,
  quantity = 1,
  addons = { hasGreetingCard: false, hasGiftBox: false },
} = defineProps<{
  /** null = no amount chosen yet - value and total show a placeholder */
  amount: number | null
  quantity?: number
  /** omitted = PDF only (no greeting card or gift box) */
  addons?: GiftcardAddonsSelection
}>()

const cardPrice = GIFTCARD_PERSONALIZATION_ADDON_PRICE_CENTS.GREETING_CARD / 100
const boxPrice = GIFTCARD_PERSONALIZATION_ADDON_PRICE_CENTS.GIFT_BOX / 100

const giftcardValue = computed(() =>
  amount === null ? null : amount * quantity
)

// matches cartGetters.getGiftcardAddonTotalPrice: value-based gift cards
// (which is what these always are) ship one card/box per unit, so the addon
// price scales with quantity too - keep this in sync with that getter.
const cardTotalPrice = computed(() => cardPrice * quantity)
const boxTotalPrice = computed(() => boxPrice * quantity)

// without a giftcard value there's no meaningful total yet
const total = computed(() => {
  if (giftcardValue.value === null) return null
  let value = giftcardValue.value
  if (addons.hasGreetingCard) value += cardTotalPrice.value
  if (addons.hasGiftBox) value += boxTotalPrice.value
  return value
})

const formatAmount = (value: number | null) =>
  value === null
    ? t('personalization.noAmount')
    : formatMoney(value, locale.value, 'EUR', 2)
</script>
